import {Trans, useTranslation} from "ni18n";
import React, {useRef} from "react";

export type ImageItem = {
  src: string;
  absSrc?: string;
  alt: string;
};

export type Bio = {
  order: number;
  name: string;
  title: string;
  email?: string;
  ref: React.MutableRefObject<any>;
  linkedIn?: string;
  twitter?: string;
  interests?: string[];
  images: ImageItem[];
  bio: string | JSX.Element;
};

export const defaultImage = {
  src: "about/default",
  alt: "Carbon Health",
};

const useBiosConstants = (): {bios: Bio[]} => {
  const i18n = useTranslation();

  const bios: Bio[] = [
    {
      order: 10,
      name: "Eren Bali",
      title: i18n.t("Co-Founder & CEO"),
      ref: useRef(null),
      images: [
        {
          src: "about/eren-1",
          alt: i18n.t("Eren Bali playing in virtual reality."),
        },
        {
          src: "about/eren-2",
          alt: "Eren Bali",
        },
      ],
      email: "eren@carbonhealth.com",
      twitter: "@erenbali",
      linkedIn: "https://linkedin.com/in/erenbali",
      interests: [
        i18n.t("Computer science"),
        i18n.t("Mathematics"),
        i18n.t("Chess"),
        i18n.t("Physics"),
      ],
      bio: (
        <Trans>
          Eren leads the Carbon Health team towards his vision to democratize healthcare and make
          quality care accessible for everyone. Previously, Eren paved the way to make education
          accessible by founding Udemy, the world’s largest marketplace for online courses, with
          over 50 million students worldwide.
          <br />
          <br />
          In his youth, Eren was a star mathematician, winning awards in mathematics, physics,
          computer science and chess including the International Mathematical Olympiads. He still
          loves chess today and plays to unwind after a long day at the office.
        </Trans>
      ),
    },
    {
      order: 110,
      name: "Ali Hartley",
      title: i18n.t("Chief Legal & People Officer"),
      ref: useRef(null),
      email: "ali@carbonhealth.com",
      linkedIn: "https://www.linkedin.com/in/ali-hartley-883890/",
      images: [
        {
          src: "about/ali-1",
          alt: i18n.t("Ali Hartley with a volleyball and swim goggles"),
        },
        {
          src: "about/ali-2",
          alt: "Ali Hartley",
        },
      ],
      interests: [
        i18n.t("Swimming"),
        i18n.t("Hiking"),
        i18n.t("Camping"),
        i18n.t("Live country music"),
      ],
      bio: (
        <Trans>
          Ali leads the legal and compliance functions at Carbon Health. With over 15 years of
          healthcare regulatory, transactional and corporate counseling experience, she brings a
          deep knowledge and passion for the health tech and healthcare industry. Prior to Carbon
          Health, Ali served as the General Counsel for The Pill Club and an Assistant General
          Counsel for Sutter Health.
          <br />
          <br />
          Ali is a Bay Area native, former collegiate water polo player and outdoor enthusiast -
          you’ll likely find her hiking, camping or backpacking with her husband and two sons on the
          weekends.
        </Trans>
      ),
    },
    {
      order: 40,
      name: "An Le",
      title: i18n.t("Chief Business Officer"),
      ref: useRef(null),
      twitter: "@missankle",
      images: [
        {
          src: "about/an-1",
          alt: i18n.t('An reading a book called "ad hoc at home".'),
        },
        {
          src: "about/an-2",
          alt: "An Le",
        },
      ],
      interests: [i18n.t("Surviving as a parent of two toddlers"), i18n.t("Homemade eats")],
      bio: (
        <Trans>
          An leads the enterprise strategy at Carbon Health, overseeing the go-to-market teams
          building direct business relationships in both the public and private sector. An has over
          20 years of experience in technology and the enterprise SaaS business, most recently
          serving as COO of Dynamic Signal and VP of Business Development at Yammer.
          <br />
          <br />
          An loves food, people, and travel. Born in Thailand as a Vietnamese refugee, she
          immigrated to the US at the age of 3 months and has since lived in Germany, the UK,
          Australia, and New Zealand.
        </Trans>
      ),
    },

    {
      order: 120,
      name: "Ayo Omojola",
      title: "SVP of Product",
      ref: useRef(null),
      email: "ayo@carbonhealth.com",
      twitter: "@ay_o",
      images: [
        {
          src: "about/ayo-1",
          alt: i18n.t("Ayo holding cooking utensils and the book 'Cage of Souls'"),
        },
        {
          src: "about/ayo-2",
          alt: "Ayo Omojola",
        },
      ],
      interests: [
        i18n.t("Cooking"),
        i18n.t("Anime"),
        i18n.t("Afrobeat"),
        i18n.t("Sci-fi & fantasy"),
      ],
      bio: (
        <Trans>
          Ayo leads the product team at Carbon Health, using technology and design thinking to find
          innovative solutions to increase access to healthcare. Before joining Carbon Health, Ayo
          was the founding product manager for the banking team for Cash App at Square, helping to
          scale one of the fastest growing card programs in history. An angel investor, he also
          seeks wildly ambitious teams that bring about positive, generation scale changes.
          <br />
          <br />
          Ayo is known for his unconventional journeys, and even spent 1-month on a cargo ship
          sailing from Singapore to Brazil.
        </Trans>
      ),
    },

    {
      order: 70,
      name: "Myoung Cha",
      title: i18n.t("Chief Strategy Officer"),
      ref: useRef(null),
      email: "myoung.cha@carbonhealth.com",
      twitter: "@cha_myoung",
      images: [
        {
          src: "about/myoung-1",
          alt: i18n.t("Myoung with a guitar"),
        },
        {
          src: "about/myoung-2",
          alt: "Myoung Cha",
        },
      ],
      bio: (
        <Trans>
          Myoung leads Carbon Health’s device-enabled home care operations, including virtual care,
          remote patient monitoring, and home diagnostics services. Myoung drives the company’s
          omnichannel care model, which is designed to meet patients where they are by delivering
          care via in-person clinics, home-based care, and easy-to-use consumer health tools.
          <br /> <br />
          Myoung has more than a decade of healthcare experience, most recently as Apple Health’s
          Head of Strategic Initiatives, where he led the launch of programs like COVID-19 exposure
          notifications with Google, the Apple Watch–based LumiHealth wellness program, and the
          Heartline Study with Johnson & Johnson.
        </Trans>
      ),
    },
    {
      order: 30,
      name: "Caesar Djavaherian, MD",
      title: i18n.t("Co-Founder, Chief Clinical Innovation Officer"),
      ref: useRef(null),
      twitter: "@caesardjava",
      email: "caesar@carbonhealth.com",
      images: [
        {
          src: "about/caesar-1",
          alt: i18n.t("Caesar with a snowboard"),
        },
        {
          src: "about/caesar-2",
          alt: "Caesar Djavaherian, MD",
        },
      ],
      interests: [i18n.t("Snowboarding"), i18n.t("Biohacking"), i18n.t("Evidence-based Medicine")],
      bio: (
        <Trans>
          Caesar brings clinical innovation to Carbon Health through technology, product
          development, and partnerships with transformative companies that improve the healthcare
          ecosystem. He is an emergency medicine physician, former high school teacher, and a
          reformed academic researcher. Prior to Carbon Health, Caesar co-founded Direct Urgent Care
          to deliver technology-enabled urgent care throughout the SF Bay Area. He began his career
          at New York-Presbyterian Hospital, The University Hospital of Columbia, and Weill Cornell
          Medicine.
          <br /> <br />
          In his spare time, Caesar advises healthcare startups, cheers on the Warriors, tries
          various HIIT workouts and daydreams about what the future will look like.
        </Trans>
      ),
    },
    {
      order: 130,
      name: "Carrie Douglas",
      title: i18n.t("General Manager, Clinics"),
      ref: useRef(null),
      email: "carrie@carbonhealth.com",
      interests: [i18n.t("Camping"), i18n.t("Traveling"), i18n.t("Swimming")],
      images: [
        {
          src: "about/carrie-1",
          alt: i18n.t("Carrie with cooking utensils"),
        },
        {
          src: "about/carrie-2",
          alt: "Carrie Douglas",
        },
      ],
      bio: (
        <Trans>
          Carrie leads the clinic teams who provide care every day to Carbon Health's patients. She
          is passionate about leading teams, teaching, coaching, and helping others grow and has
          spent her career in healthcare operations and leadership roles. Prior to Carbon Health,
          Carrie was with One Medical for over 10 years, as well as an early stage startup focused
          on value-based care for seniors.
          <br /> <br />
          Carrie loves hosting gatherings for friends and her large family - she is one of five
          kids! She has an amazing husband and two children, who all love to spend time outdoors.
        </Trans>
      ),
    },
    {
      order: 140,
      name: "Kerem Ozkay",
      title: i18n.t("Chief Operating Officer"),
      ref: useRef(null),
      email: "kozkay@carbonhealth.com",
      twitter: "@kozkay",
      interests: [i18n.t("Photography"), i18n.t("Hiking & nautre"), i18n.t("Fishing")],
      images: [
        {
          src: "about/keremo-1",
          alt: i18n.t("Kerem holding a camera"),
        },
        {
          src: "about/keremo-2",
          alt: i18n.t("Kerem Ozkay"),
        },
      ],
      bio: (
        <Trans>
          Through innovative campaigns and initiatives, Kerem and his team continue to push the
          boundaries of what people think healthcare is and what it could be. Kerem brings more than
          15 years of experience in data-driven, direct-to-consumer advertising, and omnichannel
          marketing and is passionate about furthering Carbon Health's mission of making
          high-quality healthcare accessible. Previously the VP of marketing at Z Gallerie, Kerem
          offers unique insights from the world of retail.
          <br />
          <br />
          Kerem loves photography (astrophotography is a particular area of interest), all kinds of
          fishing, and spending time with the family.
        </Trans>
      ),
    },
    {
      order: 150,
      name: "Kerem Suer",
      title: "VP of Design",
      ref: useRef(null),
      email: "kerem@carbonhealth.com",
      images: [
        {
          src: "about/kerems-1",
          alt: i18n.t("Kerem in a life vest"),
        },
        {
          src: "about/kerems-2",
          alt: i18n.t("Kerem Suer"),
        },
      ],
      bio: (
        <Trans>
          Kerem oversees the design team at Carbon Health, bringing his wealth of experience in
          designing products and brands such as Fitbit, Omada Health and MyFitnessPal. He is an
          interdisciplinary designer that creates and defines visual and interactive design systems
          across web, mobile and desktop.
          <br />
          <br />
          Kerem’s curious nature comes from his unique life adventures, starting with his time in
          the Turkish National Sailing Team. He then moved to a little town of 2,000 in Oklahoma as
          an exchange student, and has been exploring the US and the world since.
        </Trans>
      ),
    },
    {
      order: 160,
      name: "Roger Wu, MD",
      title: "National Medical Director",
      ref: useRef(null),
      email: "roger@carbonhealth.com",
      twitter: "@RWuMD",
      interests: [
        i18n.t("Climbing"),
        i18n.t("Cycling"),
        i18n.t("Surfing"),
        i18n.t("Clinical education"),
      ],
      images: [
        {
          src: "about/roger-1",
          alt: i18n.t("Roger in gi"),
        },
        {
          src: "about/roger-2",
          alt: i18n.t("Roger Wu"),
        },
      ],
      bio: (
        <Trans>
          Roger works to optimize the patient-provider relationship at Carbon Health for seamless,
          compassionate bedside care. As a practicing physician and leader, he enjoys living at the
          intersection of medicine, leadership, technology, and systems design. Roger is
          board-certified in Emergency Medicine, and prior to Carbon Health, served as an emergency
          department medical director at TeamHealth, where he was awarded Medical Director of the
          Year.
          <br /> <br />
          Outside of medicine, he can be found on the mats at his local Brazilian jiu-jitsu gym in
          San Francisco or exploring the outdoors with his wife and son.
        </Trans>
      ),
    },
    {
      order: 20,
      name: "Russ Fradin",
      title: i18n.t("Vice Chairman"),
      ref: useRef(null),
      email: "russ@carbonhealth.com",
      images: [
        {
          src: "about/russ-1",
          alt: i18n.t("Russ sitting with his phone."),
        },
        {
          src: "about/russ-2",
          alt: "Russ Fradin",
        },
      ],
      twitter: "@rfradin",
      interests: [i18n.t("His kids"), i18n.t("Running"), i18n.t("The entire Keanu Reeves catalog")],
      bio: (
        <Trans>
          Russ is a technology founder, executive, and investor who has been working across the
          startup industry since 1996. Based in Silicon Valley, Russ currently serves as Vice
          Chairman of Carbon Health where he works with Eren and the leadership team to support
          Carbon Health's historic growth. Russ is also the Chairman of Dynamic Signal, a venture
          backed SaaS business he founded and ran as CEO for ten years. Prior to founding Dynamic
          Signal, he was the founder and CEO of Adify, which he sold to Cox Enterprises in 2008.
          Russ is also on the board of CoachArt, a non-profit in California.
        </Trans>
      ),
    },
    {
      order: 90,
      name: "Sujal Mandavia, MD",
      title: i18n.t("Chief Medical Officer"),
      ref: useRef(null),
      email: "sujal@carbonhealth.com",
      interests: [i18n.t("Hocket"), i18n.t("Hiking"), i18n.t("listening to The Tragically Hip")],
      images: [
        {
          src: "about/sujal-1",
          alt: i18n.t("Sujal with a stethoscope"),
        },
        {
          src: "about/sujal-2",
          alt: i18n.t("Sujal Mandavia"),
        },
      ],
      bio: (
        <Trans>
          Sujal oversees all clinical operations and protocols, as well as the expansion of Carbon
          Health’s high-quality clinical care offering. Actively practicing for over 20 years, Sujal
          brings a wealth of healthcare management experience and has served as an examiner for the
          American Board of Emergency Medicine and on the board of the California ACEP (American
          College of Emergency Physicians). He most recently served as Senior Vice President at
          TeamHealth, where he supported 2,000 providers across the western U.S.
          <br />
          <br />
          When not at work, Sujal enjoys everything hockey related, listening to the Tragically Hip,
          and being taken for hikes by his Rhodesian Ridgebacks.
        </Trans>
      ),
    },
  ].map(bio => ({...bio, images: bio.images.length ? bio.images : [defaultImage]}));

  return {bios};
};

export default useBiosConstants;
